import styled from '@emotion/styled'
import { CardGiftcard } from '@mui/icons-material'
import { FreeDropinTicketTrigger, type FreeDropinTicketRule } from '../../../pure-js/types/FreeDropinTicketTypes'
import type { Client } from '../../../pure-js/types/GrooverTypes'
import type { FreeDropinRewardPreview as RewardPreview } from '../hooks/useFreeDropinRewardPreview'
import { formatGrooverText, getGrooverText } from '../libs/GrooverTextRepository'
import Box from './Box'
import GrooverText from './GrooverText'

type Props = {
  preview: RewardPreview
  client?: Client
}

const triggerTextKey: Record<FreeDropinTicketTrigger, string> = {
  [FreeDropinTicketTrigger.OnRegistration]: 'registrationStep2FreeDropinTriggerRegistration',
  [FreeDropinTicketTrigger.OnApproval]: 'registrationStep2FreeDropinTriggerApproval',
  [FreeDropinTicketTrigger.OnPayment]: 'registrationStep2FreeDropinTriggerPayment'
}

const getRuleName = (rule: FreeDropinTicketRule, client?: Client): string =>
  rule.name || getGrooverText('registrationStep2FreeDropinRewardFallbackName', undefined, client?.id)

const getUpsellTextKey = (entitledTickets: number, additionalQualifyingActivities: number): string => {
  const isSingle = additionalQualifyingActivities === 1
  if (entitledTickets > 0) {
    return isSingle ? 'registrationStep2FreeDropinUpsellSingle' : 'registrationStep2FreeDropinUpsellPlural'
  }
  return isSingle ? 'registrationStep2FreeDropinUpsellNotYetSingle' : 'registrationStep2FreeDropinUpsellNotYetPlural'
}

const FreeDropinRewardPreview = ({ preview, client }: Props) => {
  if (preview.rows.length === 0) return null

  return (
    <Box fullWidth gap="12px" top spacing="12px" aria-live="polite">
      {preview.rows.map((row) => (
        <RewardCard fullWidth fullPadding spacing="14px" gap="10px" key={`${row.rule.id}-${row.periodKey}`}>
          <Box fullWidth direction="row" align="center" justify="space-between" gap="8px">
            <RewardName direction="row" align="center" gap="6px">
              <CardGiftcard sx={{ fontSize: 16 }} />
              <span>{getRuleName(row.rule, client)}</span>
            </RewardName>
            {row.entitledTickets > 0 && (
              <RewardCount>
                {formatGrooverText(
                  getGrooverText('registrationStep2FreeDropinRewardCountSingle', undefined, client?.id),
                  [String(row.entitledTickets)]
                )}
              </RewardCount>
            )}
          </Box>
          {/* Activation timing only applies once clips are actually earned. */}
          {row.entitledTickets > 0 && (
            <Detail>{getGrooverText(triggerTextKey[row.rule.trigger], undefined, client?.id)}</Detail>
          )}
          {row.nextThreshold && (
            <Upsell>
              {formatGrooverText(
                getGrooverText(
                  getUpsellTextKey(row.entitledTickets, row.nextThreshold.additionalQualifyingActivities),
                  undefined,
                  client?.id
                ),
                [
                  String(row.nextThreshold.additionalQualifyingActivities),
                  String(row.nextThreshold.additionalTickets),
                  String(row.nextThreshold.totalTickets)
                ]
              )}
            </Upsell>
          )}
        </RewardCard>
      ))}
      {preview.totalTickets > 0 && (
        <TotalRow fullWidth direction="row" align="center" justify="space-between" fullPadding spacing="12px">
          <GrooverText textKey="registrationStep2FreeDropinTotal" customerId={client?.id} />
          <span>{preview.totalTickets}</span>
        </TotalRow>
      )}
    </Box>
  )
}

const RewardCard = styled(Box)`
  border-radius: 8px;
  background-color: #f8fbf5;
  border: 1px solid #d8e9ca;
`

const RewardName = styled(Box)`
  color: #0c7d26;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
`

const RewardCount = styled.span`
  flex-shrink: 0;
  border-radius: 8px;
  background-color: #70ab37;
  color: white;
  font-size: 12px;
  padding: 4px 10px;
`

const Detail = styled.span`
  color: #5f6368;
  font-size: 11px;
  line-height: 1.45;
`

const Upsell = styled.span`
  display: block;
  border-radius: 6px;
  background-color: rgba(0, 119, 24, 0.08);
  color: #0c7d26;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.45;
  padding: 10px 12px;
`

const TotalRow = styled(Box)`
  border-radius: 8px;
  background-color: #f0f7eb;
  color: #0c7d26;
  font-size: 13px;
  font-weight: 700;
`

export default FreeDropinRewardPreview
